/**
 * Response that comes from the server.
 * The server sends .json, this is just a typed version of it.
 */
export interface GetOfferDetailsResponse {
    customer: Customer;
    mortgages: Array<Mortgage>;
    operationStatus: OperationStatus;
}

/**
 * Central object.
 * One fixed-length mortgage, a candidate for extension that can be configured from the customer using this app.
 */
export interface Mortgage {
    id: number;
    location: string;
    amount: number;
    amountMaturity: number;
    ownerName: string;
    duration: string;
    expirationDate: string;
    productType: string;
    currency: string;
    interestRate: number;
    interestRateFrequency: string;
    interestRateUsage: string;
    positionInterest: string;
    ibanInterest: string;
    positionCapital: string;
    ibanCapital: string;
    mortgagePosition: string;
    showInsuranceDeath: boolean;
    signatureDate: string;
    offers: Array<Offer>;
    amortizations: Array<Amortization>;
    otherLiabilities: Array<OtherMortgage>;
    monthlyPayment: number;
    soko: number;
    repayment: number;
    totalSumLiabilities: number;
    totalSumOtherLiabilities: number;
    totalSumLiabilitiesMonthly: number;
    totalSumOtherLiabilitiesMonthly: number;
    includeLifeInsuranceOffer?: boolean;
}

/**
 * One of many potential offers preconfigured from the client advisor.
 * The customer gets different offers based on the preferences he configures using this app.
 */
export interface Offer {
    duration: number;
    value: number;
    soko: number;
    client: number;
}

/**
 * Amortization is paying off the mortgage debt.
 */
export interface Amortization {
    type: string;
    period: string;
    amount: number;
    monthlyAmount: number;
}

/**
 * Other mortgages (fixed or variable length) this customer may already have at the bank.
 * Mostly used in the graphics overview screen (details view).
 */
export interface OtherMortgage {
    mortgagePosition: string;
    amount: number;
    duration: number;
    expirationDate: string;
    interestRate: number;
    currency: string;
    productType: string;
    amortizations: Array<Amortization>;
}

/**
 * Details about the currently logged-in customer (BP, natural person etc).
 */
export interface Customer {
    firstName: string;
    lastName: string;
}

/**
 * The result of the back-end operation.
 * failedReason will be set only if status is Failed.
 */
export interface OperationStatus {
    status: ResponseStatus;
    failedReason: string;
}

/**
 * Possible response states from the server.
 */
export enum ResponseStatus {
    Success = "SUCCESS",
    Failed = "FAILED",
}

/**
 * A mortgage that the user has configured and wishes to extend.
 * This entity will be sent to the back-end after the entire process is done.
 */
export interface ConfiguredOffer {
    id: number;
    duration: string;
    amount: number;
    repayment: number;
    interestRate: number;
    lifeInsurance: boolean;
}
